import { Button, Card, Flex, Tag, theme, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import { DateTimeCell, MonoValue } from 'src/components'

import { isOwnerRole } from '../WorkspaceRolesPage/constants'
import type { OrganizationRoleRetrieve } from '../WorkspaceRolesPage/types'

const { Paragraph, Text, Title } = Typography

type RoleDetailCardProps = {
  role: OrganizationRoleRetrieve
  memberCount: number
  onEdit: () => void
  onArchive: () => void
  /** False when the viewer holds `roles.view` but not `roles.manage`. */
  canManage?: boolean
}

export const RoleDetailCard = ({
  role,
  memberCount,
  onEdit,
  onArchive,
  canManage = true
}: RoleDetailCardProps) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const isOwner = isOwnerRole(role.codename)

  return (
    <Card variant="outlined">
      <Flex vertical gap={16}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
          <Flex align="center" gap={8}>
            <Title level={4} style={{ margin: 0 }}>
              {role.name}
            </Title>
            {isOwner ? (
              <Tag color="purple">{t('workspace.orgRoles.types.owner')}</Tag>
            ) : role.is_admin ? (
              <Tag color="blue">{t('workspace.orgRoles.types.admin')}</Tag>
            ) : (
              <Tag>{t('workspace.orgRoles.types.custom')}</Tag>
            )}
          </Flex>
          {!isOwner && canManage && (
            <Flex gap={8} wrap="wrap">
              <Button onClick={onEdit}>{t('workspace.roleDetail.edit')}</Button>
              <Button
                onClick={onArchive}
                style={{
                  color: token.colorWarning,
                  borderColor: token.colorWarning
                }}
              >
                {t('workspace.orgRoles.actions.replaceAndDelete')}
              </Button>
            </Flex>
          )}
        </Flex>

        {role.description ? (
          <Paragraph type="secondary" style={{ margin: 0 }}>
            {role.description}
          </Paragraph>
        ) : null}

        <Flex gap={24} wrap="wrap">
          {role.codename ? (
            <Flex align="center" gap={6}>
              <Text type="secondary">
                {t('workspace.orgRoles.form.codename')}:
              </Text>
              <MonoValue copyable={false}>{role.codename}</MonoValue>
            </Flex>
          ) : null}
          <Flex align="center" gap={6}>
            <Text type="secondary">
              {t('workspace.orgRoles.columns.permissions')}:
            </Text>
            <Text strong>
              {role.is_admin
                ? t('workspace.orgRoles.permissionsAll')
                : role.permissions.length}
            </Text>
          </Flex>
          <Flex align="center" gap={6}>
            <Text type="secondary">{t('workspace.roleDetail.members')}:</Text>
            <Text strong>{memberCount}</Text>
          </Flex>
          <Flex align="center" gap={6}>
            <Text type="secondary">{t('workspace.roleDetail.created')}:</Text>
            <DateTimeCell value={role.date_created} variant="date" />
          </Flex>
        </Flex>
      </Flex>
    </Card>
  )
}
